/** Requests Inbox — unified queue for website inquiries, intake submissions & quote log.
 *  Manual entry (all fields optional) → edit inline → quote & send → confirm to booking. */
import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Inbox, Plus, X, ChevronDown, Send, CheckCircle2, Trash2, Flag, Loader2, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString()}`;

const calcHours = (a, b) => {
  if (!a || !b) return 0;
  const [h1, m1] = a.split(':').map(Number);
  const [h2, m2] = b.split(':').map(Number);
  let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (mins <= 0) mins += 1440;
  return Math.round(mins / 30) / 2;
};

const SOURCE_META = {
  inquiry: { label: 'Inquiry', cls: 'bg-blue-100 text-blue-700' },
  intake: { label: 'Intake Form', cls: 'bg-violet-100 text-violet-700' },
  quote: { label: 'Quote', cls: 'bg-amber-100 text-amber-700' },
};
const STATUS_CLS = (s) => ({
  new: 'bg-blue-100 text-blue-700', quoted: 'bg-amber-100 text-amber-700', sent: 'bg-amber-100 text-amber-700',
  converted: 'bg-emerald-100 text-emerald-700', confirmed: 'bg-emerald-100 text-emerald-700', booked: 'bg-emerald-100 text-emerald-700',
  lost: 'bg-gray-200 text-gray-500',
}[s] || 'bg-gray-100 text-gray-600');

const normalize = (inqs, quotes, intakes) => {
  const rows = [];
  for (const q of inqs) rows.push({
    source: 'inquiry', id: q.inquiry_id, name: q.client_name, phone: q.client_phone, email: q.client_email,
    event_date: q.event_date, pickup_time: q.pickup_time || '', dropoff_time: q.dropoff_time || '', vehicle: q.vehicle || '',
    quoted_amount: q.quoted_amount || '', notes: q.notes || q.message || '', status: q.status || 'new',
    follow_up: !!q.follow_up, created_at: q.created_at || q.updated_at || '', extra: q.service_type || '',
  });
  for (const q of quotes) rows.push({
    source: 'quote', id: q.quote_id, name: q.client_name, phone: q.client_phone, email: q.client_email,
    event_date: q.event_date, pickup_time: q.pickup_time || '', dropoff_time: q.dropoff_time || '', vehicle: q.vehicle_name || q.vehicle || '',
    quoted_amount: q.grand_total || q.quoted_amount || '', notes: q.notes || '', status: q.status || 'sent',
    follow_up: !!q.follow_up, created_at: q.created_at || '', extra: q.hours ? `${q.hours}hr` : '',
  });
  for (const s of intakes) rows.push({
    source: 'intake', id: s.submission_id, name: s.client_name, phone: s.client_phone, email: s.client_email,
    event_date: s.event_date || '', pickup_time: s.pickup_time || '', dropoff_time: s.dropoff_time || '', vehicle: s.vehicle || '',
    quoted_amount: s.quoted_amount || '', notes: s.notes || s.admin_notes || '', status: s.status || 'new',
    follow_up: !!s.follow_up, created_at: s.created_at || '', extra: s.form_type || '',
  });
  return rows.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
};

const saveRow = async (row, patch) => {
  const urls = {
    inquiry: `${API}/api/ops/inquiries/${row.id}`,
    quote: `${API}/api/command-center/quote-log/${row.id}`,
    intake: `${API}/api/intake/submissions/${row.id}`,
  };
  const bodyMap = row.source === 'quote'
    ? { client_name: patch.name, client_phone: patch.phone, client_email: patch.email, event_date: patch.event_date, pickup_time: patch.pickup_time, dropoff_time: patch.dropoff_time, vehicle_name: patch.vehicle, grand_total: Number(patch.quoted_amount) || 0, notes: patch.notes, status: patch.status, follow_up: !!patch.follow_up }
    : { client_name: patch.name, client_phone: patch.phone, client_email: patch.email, event_date: patch.event_date, pickup_time: patch.pickup_time, dropoff_time: patch.dropoff_time, vehicle: patch.vehicle, quoted_amount: Number(patch.quoted_amount) || 0, notes: patch.notes, status: patch.status, follow_up: !!patch.follow_up };
  const r = await fetch(urls[row.source], {
    method: row.source === 'inquiry' ? 'PATCH' : 'PUT',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(bodyMap),
  });
  if (!r.ok) throw new Error('Save failed');
};

const RequestRow = ({ row, reload }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ ...row });
  useEffect(() => { setF({ ...row }); }, [row]);
  const [quoteMsg, setQuoteMsg] = useState('');
  const [showQuote, setShowQuote] = useState(false);
  const hrs = calcHours(f.pickup_time, f.dropoff_time);
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));

  const buildQuoteMsg = () => {
    setQuoteMsg([
      `Here's your ACE quote${f.vehicle ? ` for the ${f.vehicle}` : ''}:`,
      f.event_date ? `Date: ${f.event_date}` : null,
      f.pickup_time ? `Pickup: ${f.pickup_time}${f.dropoff_time ? ` — Drop-off: ${f.dropoff_time} (${hrs} hrs)` : ''}` : null,
      f.quoted_amount ? `Total: ${fmt$(f.quoted_amount)} (incl. tax & gratuity)` : null,
      `Reply CONFIRM and we'll lock in your reservation.`,
    ].filter(Boolean).join('\n'));
    setShowQuote(true);
  };

  const doSave = async (patch = {}, msg = 'Saved') => {
    setBusy(true);
    try { await saveRow(row, { ...f, ...patch }); toast.success(msg); reload(); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const sendQuote = async () => {
    setBusy(true);
    let sent = false;
    try {
      if (f.email) {
        const r = await fetch(`${API}/api/ops/send-quote-email`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: f.email, client_name: (f.name || 'there').split(' ')[0], message: quoteMsg }) });
        const d = await r.json().catch(() => ({}));
        if (d.email_sent) { toast.success(d.message); sent = true; } else toast.error(d.message || 'Email failed');
      }
      if (f.phone) {
        const r = await fetch(`${API}/api/command-center/quo/send-sms`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: f.phone, message: quoteMsg }) });
        const d = await r.json().catch(() => ({}));
        const smsOk = r.ok && d.success !== false && d.sent !== false && !d.error;
        if (smsOk) { toast.success('Quote sent by SMS'); sent = true; } else toast.error(d.error || d.message || 'SMS failed');
      }
      if (!f.email && !f.phone) toast.error('Add an email or phone to send the quote');
      if (sent) { await saveRow(row, { ...f, status: 'quoted' }); setShowQuote(false); reload(); }
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const confirmBooking = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/confirmed-bookings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: f.name || 'Guest', client_phone: f.phone || '', client_email: f.email || '',
          event_date: f.event_date || new Date().toISOString().slice(0, 10),
          pickup_time: f.pickup_time || '', vehicle: f.vehicle || '', hours: hrs || 0,
          total: Number(f.quoted_amount) || 0, cc_status: 'upcoming',
          notes: [f.notes, f.dropoff_time ? `Drop-off ${f.dropoff_time}` : null, `Source: ${row.source} ${row.id}`].filter(Boolean).join(' | '),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Booking failed');
      await saveRow(row, { ...f, status: 'converted' });
      toast.success(`Booking ${d.booking_id || ''} confirmed — it's in your reservations & calendar`, { duration: 4000 });
      reload();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm('Delete this request?')) return;
    const urls = { inquiry: `${API}/api/ops/inquiries/${row.id}`, quote: `${API}/api/command-center/quote-log/${row.id}`, intake: null };
    if (row.source === 'intake') { await saveRow(row, { ...f, status: 'deleted' }); } else { await fetch(urls[row.source], { method: 'DELETE' }); }
    toast.success('Deleted'); reload();
  };

  const meta = SOURCE_META[row.source];
  return (
    <Card className="border-gray-200 overflow-hidden" data-testid={`req-${row.source}-${row.id}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full p-3 text-left hover:bg-gray-50" data-testid={`req-toggle-${row.id}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold truncate flex items-center gap-2">
              {row.follow_up && <Flag className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
              {row.name || '(no name)'}
              <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </p>
            <p className="text-[11px] text-gray-500 truncate">{[row.vehicle, row.event_date, row.phone, row.extra].filter(Boolean).join(' · ') || String(row.created_at).slice(0, 10)}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {row.quoted_amount ? <span className="text-sm font-black">{fmt$(row.quoted_amount)}</span> : null}
            <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
            <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_CLS(row.status)}`}>{row.status}</span>
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t bg-gray-50 p-3 space-y-3" data-testid={`req-editor-${row.id}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[['name', 'Name', 'text'], ['phone', 'Phone', 'text'], ['email', 'Email', 'text'], ['event_date', 'Date', 'date'],
              ['pickup_time', 'Pickup Time', 'time'], ['dropoff_time', 'Drop-off Time', 'time'], ['vehicle', 'Vehicle', 'text'], ['quoted_amount', 'Quote $', 'number']].map(([k, lbl, type]) => (
              <div key={k}><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">{lbl}</label>
                <Input type={type} value={f[k] || ''} onChange={e => s(k, e.target.value)} className="h-8 text-xs bg-white" data-testid={`req-edit-${k}-${row.id}`} /></div>
            ))}
          </div>
          {hrs > 0 && <p className="text-[10px] text-emerald-600 font-bold">Auto-calculated duration: {hrs} hours</p>}
          <div><label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Notes / Message</label>
            <textarea value={f.notes || ''} onChange={e => s('notes', e.target.value)} rows={2} className="w-full text-xs border rounded px-2 py-1.5 bg-white" data-testid={`req-edit-notes-${row.id}`} /></div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={() => doSave()} disabled={busy} className="h-8 text-xs bg-gray-900 hover:bg-black" data-testid={`req-save-${row.id}`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}</Button>
            <Button size="sm" onClick={buildQuoteMsg} variant="outline" className="h-8 text-xs" data-testid={`req-quote-${row.id}`}><Send className="h-3 w-3 mr-1" />Quote &amp; Send</Button>
            <Button size="sm" onClick={confirmBooking} disabled={busy} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`req-confirm-${row.id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Confirm → Booking</Button>
            <button onClick={() => { const nv = !f.follow_up; s('follow_up', nv); doSave({ follow_up: nv }, nv ? 'Marked waiting on client — shows on dashboard' : 'Follow-up cleared'); }}
              className={`h-8 text-[10px] uppercase tracking-wider font-bold px-3 rounded border ${f.follow_up ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-white border-gray-300 text-gray-500'}`}
              data-testid={`req-followup-${row.id}`}><Flag className="h-3 w-3 inline mr-1 -mt-0.5" />{f.follow_up ? 'Waiting on Client' : 'Flag Follow-up'}</button>
            <button onClick={remove} className="h-8 px-2 text-gray-400 hover:text-red-600 ml-auto" data-testid={`req-delete-${row.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>

          {showQuote && (
            <div className="bg-white border border-red-200 rounded-lg p-3 space-y-2" data-testid={`req-quote-panel-${row.id}`}>
              <p className="text-[10px] uppercase tracking-wider text-red-600 font-bold">Customize &amp; Send Quote {f.email ? `→ ${f.email}` : ''} {f.phone ? `· SMS ${f.phone}` : ''}</p>
              <textarea value={quoteMsg} onChange={e => setQuoteMsg(e.target.value)} rows={5} className="w-full text-xs border rounded px-2 py-1.5" data-testid={`req-quote-msg-${row.id}`} />
              <div className="flex gap-2">
                <Button size="sm" onClick={sendQuote} disabled={busy} className="h-8 text-xs bg-red-600 hover:bg-red-700" data-testid={`req-quote-send-${row.id}`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Send className="h-3 w-3 mr-1" />Send Quote</>}</Button>
                <Button size="sm" variant="outline" onClick={() => setShowQuote(false)} className="h-8 text-xs">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const RequestsInboxTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [nf, setNf] = useState({ client_name: '', client_phone: '', client_email: '', event_date: '', pickup_time: '', dropoff_time: '', vehicle: '', message: '' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([
        fetch(`${API}/api/ops/inquiries`).then(r => r.json()).catch(() => ({})),
        fetch(`${API}/api/command-center/quote-log`).then(r => r.json()).catch(() => ({})),
        fetch(`${API}/api/intake/submissions?limit=200`).then(r => r.json()).catch(() => ({})),
      ]);
      setRows(normalize(a.inquiries || [], b.quotes || [], (c.submissions || []).filter(s => s.status !== 'deleted')));
    } catch (_) {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const createRequest = async () => {
    if (!nf.client_name && !nf.client_phone && !nf.client_email) { toast.error('Add at least a name, phone or email'); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/ops/inquiries`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...nf, source: 'manual' }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Create failed');
      toast.success('Request added');
      setShowNew(false);
      setNf({ client_name: '', client_phone: '', client_email: '', event_date: '', pickup_time: '', dropoff_time: '', vehicle: '', message: '' });
      load();
    } catch (e) { toast.error(e.message); }
    setCreating(false);
  };

  const OPEN = ['new', 'contacted', 'quoted', 'sent', 'reviewed', 'follow_up'];
  const filtered = rows.filter(r => {
    if (filter === 'open' && !OPEN.includes(r.status)) return false;
    if (filter === 'followup' && !r.follow_up) return false;
    if (filter === 'converted' && !['converted', 'confirmed', 'booked'].includes(r.status)) return false;
    if (q && !`${r.name} ${r.phone} ${r.email} ${r.vehicle}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const nfHrs = calcHours(nf.pickup_time, nf.dropoff_time);

  return (
    <Card className="p-5 border-gray-200" data-testid="requests-inbox">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-lg font-black flex items-center gap-2"><Inbox className="h-5 w-5 text-red-600" />Requests Inbox</h2>
          <p className="text-[11px] text-gray-500">Website inquiries · intake forms · quotes — edit, quote &amp; confirm from one queue</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="h-8 text-xs pl-8 w-40" data-testid="req-search" />
          </div>
          <Button size="sm" onClick={() => setShowNew(o => !o)} className="h-8 text-xs bg-red-600 hover:bg-red-700" data-testid="req-new-btn">
            {showNew ? <><X className="h-3 w-3 mr-1" />Cancel</> : <><Plus className="h-3 w-3 mr-1" />New Request</>}
          </Button>
        </div>
      </div>

      <div className="flex gap-1.5 mb-4 flex-wrap">
        {[['open', 'Open'], ['followup', 'Waiting on Client'], ['converted', 'Converted'], ['all', 'All']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full ${filter === v ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`} data-testid={`req-filter-${v}`}>{l}</button>
        ))}
      </div>

      {showNew && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 space-y-2" data-testid="req-new-form">
          <p className="text-[10px] uppercase tracking-wider text-red-700 font-bold">Manual request — every field optional</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Input placeholder="Client name" value={nf.client_name} onChange={e => setNf(p => ({ ...p, client_name: e.target.value }))} className="h-8 text-xs bg-white" data-testid="req-new-name" />
            <Input placeholder="Phone" value={nf.client_phone} onChange={e => setNf(p => ({ ...p, client_phone: e.target.value }))} className="h-8 text-xs bg-white" data-testid="req-new-phone" />
            <Input placeholder="Email" value={nf.client_email} onChange={e => setNf(p => ({ ...p, client_email: e.target.value }))} className="h-8 text-xs bg-white" />
            <Input type="date" value={nf.event_date} onChange={e => setNf(p => ({ ...p, event_date: e.target.value }))} className="h-8 text-xs bg-white" data-testid="req-new-date" />
            <Input type="time" title="Pickup time" value={nf.pickup_time} onChange={e => setNf(p => ({ ...p, pickup_time: e.target.value }))} className="h-8 text-xs bg-white" data-testid="req-new-pickup-time" />
            <Input type="time" title="Drop-off time" value={nf.dropoff_time} onChange={e => setNf(p => ({ ...p, dropoff_time: e.target.value }))} className="h-8 text-xs bg-white" data-testid="req-new-dropoff-time" />
            <Input placeholder="Vehicle" value={nf.vehicle} onChange={e => setNf(p => ({ ...p, vehicle: e.target.value }))} className="h-8 text-xs bg-white" data-testid="req-new-vehicle" />
            <Input placeholder="Notes" value={nf.message} onChange={e => setNf(p => ({ ...p, message: e.target.value }))} className="h-8 text-xs bg-white" />
          </div>
          {nfHrs > 0 && <p className="text-[10px] text-emerald-600 font-bold">Auto-calculated duration: {nfHrs} hours</p>}
          <Button size="sm" onClick={createRequest} disabled={creating} className="h-8 text-xs bg-red-600 hover:bg-red-700 w-full" data-testid="req-new-save">
            {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Add Request'}
          </Button>
        </div>
      )}

      {loading ? <p className="text-xs text-gray-400 text-center py-8">Loading requests…</p>
        : filtered.length === 0 ? <p className="text-xs text-gray-400 text-center py-8">No requests in this view.</p>
          : <div className="space-y-2">{filtered.map(r => <RequestRow key={`${r.source}-${r.id}`} row={r} reload={load} />)}</div>}
    </Card>
  );
};

export default RequestsInboxTab;
